import Button from "@/components/Common/Button";
import useCountries from "@/hooks/useCountries";
import {usePayment} from "@/hooks/usePayment";
import {editedPaymentMethodIdAtom} from "@/store/jotaiAtoms";
import type {TBillingDetails, TCountry} from "@/store/types";
import useUserStore from "@/store/user";
import {CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {useSetAtom} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const StripeCardElement: FC<{
  agencyId?: number;
  billingDetails: TBillingDetails;
  paymentMethodId: string;
}> = ({agencyId, billingDetails, paymentMethodId}) => {
  const {t} = useTranslation("common");
  const setEditedPaymentMethodId = useSetAtom(editedPaymentMethodIdAtom);
  const loggedInUser = useUserStore(state => state.user);
  const stripe = useStripe();
  const stripeElements = useElements();
  const {countriesData} = useCountries();
  const {detachPaymentMethod, detachLoading, attachPaymentMethod, attachLoading} = usePayment({agencyId});
  const createSource = async () => {
    const {paymentMethod} = await stripe?.createPaymentMethod({
      type: "card",
      card: stripeElements?.getElement(CardNumberElement)!,
      billing_details: {
        address: {
          city: billingDetails.city,
          country: countriesData[billingDetails.country as TCountry]?.code,
          postal_code: billingDetails.postalCode,
          line1: billingDetails.street,
        },
        email: loggedInUser.email!,
        name: loggedInUser.first_name! + " " + loggedInUser.last_name!,
        phone: billingDetails.phone,
      },
    })!;

    if (paymentMethod?.id) {
      await attachPaymentMethod({paymentMethodId: paymentMethod.id});
      setEditedPaymentMethodId(null);
      await detachPaymentMethod({paymentMethodId});
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-lg border p-4">
      <div>
        <label htmlFor="cardNumber" className="text-neutral-grey-dark text-sm font-medium">
          {t("cardNumber")}
        </label>
        <CardNumberElement
          id="cardNumber"
          options={{
            showIcon: true,
            style: {
              base: {
                fontSize: "16px",
                color: "#424770",
                letterSpacing: "0.025em",
                "::placeholder": {
                  color: "#aab7c4",
                  fontSize: "16px",
                },
              },
              invalid: {
                color: "#9e2146",
              },
            },
          }}
        />
      </div>
      <div className="flex flex-row gap-8">
        <div>
          <label htmlFor="expiry" className="text-neutral-grey-dark text-sm font-medium">
            {t("expirationDate")}
          </label>
          <CardExpiryElement
            id="expiry"
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  letterSpacing: "0.025em",
                  "::placeholder": {
                    color: "#aab7c4",
                    fontSize: "12px",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
          />
        </div>
        <div>
          <label htmlFor="cvc" className="text-neutral-grey-dark text-sm font-medium">
            CVC
          </label>
          <CardCvcElement
            id="cvc"
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  letterSpacing: "0.025em",
                  "::placeholder": {
                    color: "#aab7c4",
                    fontSize: "12px",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
          />
        </div>
      </div>
      <div className="flex flex-row justify-end gap-2">
        <Button
          isDisabled={attachLoading || detachLoading}
          className="h-8 self-end bg-gray-200 px-3"
          onClick={() => setEditedPaymentMethodId(null)}>
          {t("cancel")}
        </Button>
        <Button
          isDisabled={attachLoading || detachLoading}
          buttonStyle="Blue"
          className="h-8 self-end px-3"
          onClick={createSource}>
          {attachLoading || detachLoading ? t("loading") : t("Save")}
        </Button>
      </div>
    </div>
  );
};

export default StripeCardElement;
